import { Decimal } from 'decimal.js'

import { isOption } from '@/lib/instrument-util'
import { Denomination, Position } from '@/types/generated/common/trading'

export type PriceDecimalsMap = Record<string, number>
export type LiveTicker = { markPrice?: string }

const ONE = new Decimal(1)
const HUNDRED = new Decimal(100)

function getFiniteDecimal(value: unknown): Decimal | null {
  if (value === undefined || value === null || value === '') return null
  try {
    const decimal = new Decimal(String(value))
    return decimal.isFinite() ? decimal : null
  } catch {
    return null
  }
}

function scalePositionMetricAtMark(
  metric: string | undefined,
  position: Position,
  liveMark: Decimal,
  inverse: boolean
): string | undefined {
  const metricValue = getFiniteDecimal(metric)
  const serverMark = getFiniteDecimal(position.markPrice)
  const avgPrice = getFiniteDecimal(position.avgPrice)

  if (!metricValue || !serverMark || !avgPrice) return metric

  // PnL is proportional to a basis function of mark, solved against the server
  // snapshot. Linear perps and option premiums move linearly with mark, so the
  // basis is (mark − avg). Inverse (coin-margined) perps settle in the base coin,
  // where coin PnL ∝ (1/avg − 1/mark); using (mark − avg) there over/understates
  // the live value. Fall back to linear if any price is non-positive (1/x undefined).
  const useInverse = inverse && avgPrice.gt(0) && serverMark.gt(0) && liveMark.gt(0)
  const basisAt = (mark: Decimal): Decimal =>
    useInverse ? ONE.div(avgPrice).minus(ONE.div(mark)) : mark.minus(avgPrice)

  const serverDistance = basisAt(serverMark)
  if (serverDistance.isZero()) {
    return basisAt(liveMark).isZero() ? '0' : metric
  }

  return metricValue.mul(basisAt(liveMark)).div(serverDistance).toString()
}

function calculateEntryNotional(position: Position, denomination: Denomination): Decimal | null {
  const quantity = getFiniteDecimal(position.quantity)?.abs()
  const avgPrice = getFiniteDecimal(position.avgPrice)

  if (!quantity || quantity.isZero() || !avgPrice || avgPrice.lte(0)) return null

  if (position.sizeDenomination === Denomination.DENOMINATION_QUOTE) {
    return denomination === Denomination.DENOMINATION_BASE ? quantity.div(avgPrice) : quantity
  }

  return quantity.mul(avgPrice)
}

export function calculatePositionPnlPercent(position: Position): string | undefined {
  const preferredDenomination =
    position.pnlDenomination === Denomination.DENOMINATION_BASE
      ? Denomination.DENOMINATION_BASE
      : Denomination.DENOMINATION_QUOTE

  const candidates =
    preferredDenomination === Denomination.DENOMINATION_BASE
      ? [
          {
            pnl: getFiniteDecimal(position.unrealizedPnlBase ?? position.unrealizedPnl),
            denomination: Denomination.DENOMINATION_BASE,
          },
          {
            pnl: getFiniteDecimal(position.unrealizedPnlUsd),
            denomination: Denomination.DENOMINATION_QUOTE,
          },
        ]
      : [
          {
            pnl: getFiniteDecimal(position.unrealizedPnl ?? position.unrealizedPnlUsd),
            denomination: Denomination.DENOMINATION_QUOTE,
          },
          {
            pnl: getFiniteDecimal(position.unrealizedPnlBase),
            denomination: Denomination.DENOMINATION_BASE,
          },
        ]

  for (const candidate of candidates) {
    if (!candidate.pnl) continue
    const notional = calculateEntryNotional(position, candidate.denomination)
    if (!notional || notional.isZero()) continue
    return candidate.pnl.div(notional).mul(HUNDRED).toString()
  }

  return position.pnlPercent
}

export function applyLivePositionMetrics(
  position: Position,
  liveTicker: LiveTicker | undefined
): Position {
  const liveMark = getFiniteDecimal(liveTicker?.markPrice)
  if (!liveMark || liveMark.lte(0)) {
    const pnlPercent = calculatePositionPnlPercent(position)
    return pnlPercent === position.pnlPercent ? position : { ...position, pnlPercent }
  }

  // Inverse perps/futures realise PnL in the base coin on a reciprocal price curve,
  // so their native + base PnL scale on that curve. Server stamps the denomination:
  // BASE = inverse perps + options, so excluding options isolates inverse perps.
  // USD PnL is linear in mark for every instrument type, so it always scales linearly.
  const inverseCoinPnl =
    position.pnlDenomination === Denomination.DENOMINATION_BASE && !isOption(position)

  const markPrice = liveMark.toString()
  const unrealizedPnlBase = scalePositionMetricAtMark(
    position.unrealizedPnlBase,
    position,
    liveMark,
    inverseCoinPnl
  )
  const unrealizedPnlUsd = scalePositionMetricAtMark(
    position.unrealizedPnlUsd,
    position,
    liveMark,
    false
  )
  const unrealizedPnl = scalePositionMetricAtMark(
    position.unrealizedPnl,
    position,
    liveMark,
    inverseCoinPnl
  )
  const remappedPosition = {
    ...position,
    markPrice,
    unrealizedPnl,
    unrealizedPnlBase,
    unrealizedPnlUsd,
  }
  const pnlPercent = calculatePositionPnlPercent(remappedPosition)

  if (
    markPrice === position.markPrice &&
    unrealizedPnlBase === position.unrealizedPnlBase &&
    unrealizedPnlUsd === position.unrealizedPnlUsd &&
    unrealizedPnl === position.unrealizedPnl &&
    pnlPercent === position.pnlPercent
  ) {
    return position
  }

  return {
    ...position,
    markPrice,
    unrealizedPnl,
    unrealizedPnlBase,
    unrealizedPnlUsd,
    pnlPercent,
  }
}
